<template>
  <div class="mindset360-dimension-correction-tab p-6">
    <div class="bg-red-50 border-2 border-red-300 rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold text-red-900 mb-3">
        <i class="pi pi-exclamation-triangle mr-2"></i>
        360mindset dimension correction (one-time hotfix)
      </h2>
      <div class="text-red-900 space-y-2 text-sm">
        <p class="font-semibold">
          Temporary tool — do not use again after the maximator correction is complete. Remove this tab in a follow-up change.
        </p>
        <p>
          Allowed tenant only: <code class="bg-red-100 px-1 rounded">maximator</code>
          (database <code class="bg-red-100 px-1 rounded">app_maximator</code>).
          Schema type: <code class="bg-red-100 px-1 rounded">360mindset</code>.
        </p>
        <p>
          Canonical dimension names are taken from the schema&apos;s linked dimensions. Question
          <code class="bg-red-100 px-1 rounded">dimensionName</code> values on the schema and on all
          assessments with matching <code class="bg-red-100 px-1 rounded">generatedFromSchema</code>
          are corrected, then assessments with a <code class="bg-red-100 px-1 rounded">results</code>
          object are fully re-evaluated.
        </p>
        <p class="font-medium">Run dry run first. Apply only after reviewing the mapping table.</p>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 mb-6">
      <Button
        label="Run dry run"
        icon="pi pi-search"
        :loading="loadingDryRun"
        class="p-button-info"
        @click="runDryRun"
      />
      <Button
        label="Apply correction"
        icon="pi pi-check"
        :loading="loadingApply"
        :disabled="!hasDryRunResults"
        class="p-button-success"
        @click="runApply"
      />
      <Button
        v-if="hasDryRunResults || hasApplyResults"
        label="Clear results"
        icon="pi pi-times"
        class="p-button-secondary"
        :disabled="loadingDryRun || loadingApply"
        @click="clearResults"
      />
    </div>

    <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded p-4 mb-6 text-red-800">
      {{ errorMessage }}
    </div>

    <div v-if="report" class="space-y-6">
      <div class="bg-slate-50 border border-slate-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-slate-800 mb-4">Summary</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-600">Schema ID</div>
            <div class="font-mono text-xs break-all">{{ report.schemaId }}</div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-600">Assessments</div>
            <div class="text-2xl font-bold">{{ report.totalAssessments ?? 0 }}</div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-600">With results (re-eval)</div>
            <div class="text-2xl font-bold">{{ report.assessmentsWithResults ?? 0 }}</div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-600">Mapping rows</div>
            <div class="text-2xl font-bold">{{ report.dimensionMappings?.length ?? 0 }}</div>
          </div>
        </div>
      </div>

      <div class="bg-white border border-slate-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-slate-800 mb-3">Canonical dimensions (from schema)</h3>
        <ul class="list-disc list-inside text-sm text-slate-700 space-y-1">
          <li v-for="dim in report.canonicalDimensions" :key="dim">
            <code>{{ dim }}</code>
          </li>
        </ul>
      </div>

      <div class="bg-white border border-amber-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-amber-900 mb-3">
          Dimension name corrections
        </h3>
        <p v-if="!report.dimensionMappings?.length" class="text-sm text-slate-600">
          No spelling differences found — all question dimension names already match the schema.
        </p>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm border border-slate-200">
            <thead class="bg-amber-50">
              <tr>
                <th class="text-left p-2 border-b">Incorrect name</th>
                <th class="text-left p-2 border-b">Correct name</th>
                <th class="text-left p-2 border-b">Match</th>
                <th class="text-right p-2 border-b">Schema questions</th>
                <th class="text-right p-2 border-b">Assessment questions</th>
                <th class="text-right p-2 border-b">Assessments affected</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in report.dimensionMappings"
                :key="row.incorrectName"
                class="border-b border-slate-100"
              >
                <td class="p-2 font-mono text-red-700">{{ row.incorrectName }}</td>
                <td class="p-2 font-mono text-green-700">{{ row.correctName }}</td>
                <td class="p-2 text-slate-600">{{ row.matchType }}</td>
                <td class="p-2 text-right">{{ row.schemaQuestionOccurrences }}</td>
                <td class="p-2 text-right">{{ row.assessmentQuestionOccurrences }}</td>
                <td class="p-2 text-right">{{ row.assessmentsAffected }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-if="report.dryRun === false || report.schemaQuestionsUpdated != null"
        class="bg-white border border-slate-200 rounded-lg p-6"
      >
        <h3 class="text-lg font-semibold text-slate-800 mb-3">Apply impact</h3>
        <ul class="text-sm text-slate-700 space-y-1">
          <li>Schema question fields updated: <strong>{{ report.schemaQuestionsUpdated ?? 0 }}</strong></li>
          <li>Assessment question fields updated: <strong>{{ report.assessmentQuestionsUpdated ?? 0 }}</strong></li>
          <li>Assessments re-evaluated: <strong>{{ report.assessmentsReEvaluated ?? 0 }}</strong></li>
          <li v-if="report.errors">Errors: <strong class="text-red-700">{{ report.errors }}</strong></li>
        </ul>
        <ul
          v-if="report.errorDetails?.length"
          class="mt-3 list-disc list-inside text-sm text-red-800"
        >
          <li v-for="(err, i) in report.errorDetails" :key="i">
            {{ err.assessmentId }}: {{ err.error }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import migrationService from '@/services/migrationService';

export default {
  name: 'Mindset360DimensionCorrectionTab',
  components: { Button },
  data() {
    return {
      loadingDryRun: false,
      loadingApply: false,
      dryRunReport: null,
      applyReport: null,
      errorMessage: null,
    };
  },
  computed: {
    report() {
      return this.applyReport || this.dryRunReport;
    },
    hasDryRunResults() {
      return this.dryRunReport != null;
    },
    hasApplyResults() {
      return this.applyReport != null;
    },
  },
  methods: {
    async runDryRun() {
      this.errorMessage = null;
      this.dryRunReport = null;
      this.applyReport = null;
      this.loadingDryRun = true;
      try {
        const res = await migrationService.run360mindsetDimensionCorrectionDryRun();
        this.dryRunReport = res?.data?.data ?? res?.data ?? res;
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingDryRun = false;
      }
    },
    async runApply() {
      if (!this.hasDryRunResults) return;
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Apply 360mindset dimension correction on app_maximator? This updates schema and assessment questions and re-evaluates results.',
      );
      if (!ok) return;

      this.errorMessage = null;
      this.applyReport = null;
      this.loadingApply = true;
      try {
        const res = await migrationService.run360mindsetDimensionCorrection();
        this.applyReport = res?.data?.data ?? res?.data ?? res;
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingApply = false;
      }
    },
    clearResults() {
      this.dryRunReport = null;
      this.applyReport = null;
      this.errorMessage = null;
    },
  },
};
</script>
